import React, { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt } from "@fortawesome/free-solid-svg-icons";

import { generateUUID, getObjectByKeys, promiseAllSettled } from "helpers/commonHelpers";
import { invoiceQueryKeys } from "api/queries/invoice/invoiceQueryKeys";
import { deleteInvoiceBucketAttachment, uploadFile } from "api";
import errorToast from "helpers/errorToast";
import Button from "components/atoms/Button/Button";
import useConfirm from "hooks/useConfirm";
import { ReactComponent as EditIcon } from "assets/images/iconsV2/edit-02.svg";
import InvoiceFormOtherAttachmentListItem from "./InvoiceFormOtherAttachmentListItem";
import InvoiceFormBucketAttachmentModal from "./InvoiceFormBucketAttachmentModal";
import {
  GroupedBucketAttachment,
  INVOICE_ATTACHMENT_ACCEPT,
  InvoiceFormStop,
  buildGroupedAttachmentLabel,
  groupBucketAttachmentsByFile,
  truncateAttachmentFileName,
} from "./invoiceBucketAttachmentHelpers";

type InvoiceFormOtherAttachmentListProps = {
  existingFiles: any;
  attachmentBucketId?: number;
  stopAssociationEnabled?: boolean;
  shipmentStops?: InvoiceFormStop[];
};

function InvoiceFormOtherAttachmentList(props: InvoiceFormOtherAttachmentListProps) {
  const {
    existingFiles,
    attachmentBucketId,
    stopAssociationEnabled = false,
    shipmentStops = [],
  } = props;
  const intl = useIntl();
  const { confirm, ConfirmModal } = useConfirm();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const currentInvoiceId = searchParams.get("invoice");
  const [files, setFiles] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [editingAttachment, setEditingAttachment] = useState<GroupedBucketAttachment | null>(null);

  useEffect(() => {
    if (existingFiles && !Object.keys(files).length && !stopAssociationEnabled) {
      setFiles((prev) => ({
        ...prev,
        ...getObjectByKeys(existingFiles),
      }));
    }
  }, [existingFiles, files, stopAssociationEnabled]);

  const groupedAttachments = useMemo(() => {
    if (!attachmentBucketId) return [];
    const rows = (existingFiles || []).map((file: any) => ({
      file: {
        id: file.id,
        name: file.name,
        reference_number: file.reference_number,
        url: file.url,
      },
      stop: file.stop,
      attachment_bucket: { id: attachmentBucketId },
    }));
    return groupBucketAttachmentsByFile(rows, attachmentBucketId);
  }, [attachmentBucketId, existingFiles]);

  const invalidateAttachments = () => {
    queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.invoiceAttachmentDetails(currentInvoiceId!) });
  };

  const handleFilesSelect = async (event: any) => {
    if (stopAssociationEnabled) return;
    event.persist();
    const filesData: any = {};
    const { files: selectedFiles } = event.target;

    const uploadingFiles = Array.from(selectedFiles).map((file: any) => {
      const uuid = generateUUID();
      filesData[uuid] = {
        id: uuid,
        file,
        progress: 1,
        error: false,
        errorMessage: "",
        name: file.name,
        url: "",
        abortController: new AbortController(),
      };
      return uploadFiles(filesData[uuid]);
    });
    setFiles((prev) => ({
      ...prev,
      ...filesData,
    }));
    await promiseAllSettled(uploadingFiles);
    invalidateAttachments();
  };

  const uploadFiles = (data: any) => {
    if (!data.file) return;

    const formData = new FormData();
    formData.append("file", data.file);
    formData.append("filename", data.name);
    formData.append("invoice_reference_number", currentInvoiceId!);
    if (attachmentBucketId) {
      formData.append("invoice_attachment_bucket_pk", attachmentBucketId.toString());
    }

    return uploadFile(formData, {
      onUploadProgress: (progressEvent: any) => {
        updateFiles(data.id, {
          progress: Math.round((progressEvent.loaded * 100) / progressEvent.total),
        });
      },
      signal: data.abortController.signal,
    })
      .then((res) => {
        setFiles((prev) => {
          const cloned: any = { ...prev };
          const updatedData = {
            ...cloned[data.id],
            ...res.data,
            progress: 0,
          };
          delete cloned[data.id];
          return {
            ...cloned,
            [res.data.id]: updatedData,
          };
        });
        return Promise.resolve(res.data);
      })
      .catch((error) => {
        updateFiles(data.id, { progress: 0, error: true, errorMessage: error?.response?.data?.detail || "" });
        return Promise.reject(error);
      });
  };

  const updateFiles = (id: number, data: any) => {
    setFiles((prev: any) => ({
      ...prev,
      ...(prev[id] && {
        [id]: {
          ...prev[id],
          ...data,
        },
      }),
    }));
  };

  const handleFileRemove = (data: any) => {
    setFiles((prev) => {
      const cloned: any = { ...prev };
      if (cloned[data.id]) {
        delete cloned[data.id];
      }
      return cloned;
    });
    invalidateAttachments();
  };

  const handleMultistopDelete = async (attachment: GroupedBucketAttachment): Promise<boolean> => {
    const confirmed = await confirm({
      title: intl.formatMessage({
        id: "billing.invoiceAttachment.confirmDeleteTitle",
        defaultMessage: "Delete attachment",
      }),
      message: intl.formatMessage({
        id: "billing.invoiceAttachment.confirmDeleteMessage",
        defaultMessage: "Confirm to delete attachment?",
      }),
    });
    if (!confirmed || !attachmentBucketId) return false;

    try {
      await deleteInvoiceBucketAttachment(
        currentInvoiceId!,
        attachment.file.reference_number,
        attachmentBucketId,
      );
      invalidateAttachments();
      return true;
    } catch (error) {
      errorToast(error, "Could not delete file, please try again later");
      return false;
    }
  };

  const openAddModal = () => {
    setModalMode("add");
    setEditingAttachment(null);
    setShowModal(true);
  };

  return (
    <div className="provider-other-attachments">
      <ConfirmModal />
      {stopAssociationEnabled &&
        attachmentBucketId &&
        currentInvoiceId && (
        <InvoiceFormBucketAttachmentModal
          show={showModal}
          mode={modalMode}
          invoiceId={currentInvoiceId}
          bucketId={attachmentBucketId}
          shipmentStops={shipmentStops}
          editingAttachment={editingAttachment}
          onClose={() => setShowModal(false)}
          onSuccess={invalidateAttachments}
          onDelete={handleMultistopDelete}
        />
      )}

      {stopAssociationEnabled
        ? groupedAttachments.map((attachment) => (
            <div key={attachment.file.id} className="d-flex align-items-start mb-2">
              <div className="flex-grow-1">
                <a href={attachment.file.url} target="_blank" rel="noopener noreferrer" title={attachment.file.name}>
                  {truncateAttachmentFileName(attachment.file.name)}
                </a>
                <div className="text-secondary text-xxs">{buildGroupedAttachmentLabel(attachment, intl)}</div>
              </div>
              <div className="d-flex align-items-center ml-2" style={{ height: 20, gap: 8 }}>
                <button
                  type="button"
                  className="btn btn-link p-0 d-inline-flex align-items-center justify-content-center"
                  style={{ width: 16, height: 16, lineHeight: 1 }}
                  aria-label={intl.formatMessage({
                    id: "billing.invoiceAttachment.modal.editTitle",
                    defaultMessage: "Edit attachment",
                  })}
                  onClick={() => {
                    setModalMode("edit");
                    setEditingAttachment(attachment);
                    setShowModal(true);
                  }}
                >
                  <EditIcon width={16} height={16} />
                </button>
                <button
                  type="button"
                  className="btn btn-link p-0 d-inline-flex align-items-center justify-content-center text-danger"
                  style={{ width: 16, height: 16, lineHeight: 1 }}
                  aria-label={intl.formatMessage({
                    id: "billing.invoiceAttachment.confirmDeleteTitle",
                    defaultMessage: "Delete attachment",
                  })}
                  onClick={() => handleMultistopDelete(attachment)}
                >
                  <FontAwesomeIcon icon={faTrashAlt} style={{ width: 13, height: 13 }} />
                </button>
              </div>
            </div>
          ))
        : Object.values(files).map((file: any) => (
            <InvoiceFormOtherAttachmentListItem key={file.id} currentFile={file} onRemove={handleFileRemove} />
          ))}

      <Button
        variant="primary"
        className="rounded-pill blue-200 h-26 align-self-center position-relative"
        onClick={stopAssociationEnabled ? openAddModal : undefined}
      >
        <FormattedMessage id="new-request.otherAttachment.addAttachment" defaultMessage="Add attachment" />
        {!stopAssociationEnabled && (
          <input
            type="file"
            accept={INVOICE_ATTACHMENT_ACCEPT}
            className="attachment-input-file"
            multiple
            onChange={handleFilesSelect}
          />
        )}
      </Button>
    </div>
  );
}

export default InvoiceFormOtherAttachmentList;

InvoiceFormOtherAttachmentList.defaultProps = {
  attachmentBucketId: null,
  stopAssociationEnabled: false,
  shipmentStops: [],
};
